import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, FileText, ShieldCheck } from "lucide-react";

export interface InsuranceItem {
  id: string;
  label: string;
  category?: string;
}

export interface InsuranceEntryValue {
  gesellschaft: string;
  kopie: boolean;
  foto: boolean;
  keineVorhanden: boolean;
  relevant: boolean;
}

export type InsuranceChecklistValue = Record<
  string,
  { gp1?: InsuranceEntryValue; gp2?: InsuranceEntryValue }
>;

interface InsuranceChecklistTableProps {
  items: InsuranceItem[];
  value: InsuranceChecklistValue;
  onChange: (value: InsuranceChecklistValue) => void;
  showGP2?: boolean;
  className?: string;
}

const emptyEntry = (): InsuranceEntryValue => ({
  gesellschaft: "",
  kopie: false,
  foto: false,
  keineVorhanden: false,
  relevant: true,
});

export function InsuranceChecklistTable({
  items,
  value = {},
  onChange,
  showGP2 = true,
  className,
}: InsuranceChecklistTableProps) {
  const getEntry = (itemId: string, gp: "gp1" | "gp2"): InsuranceEntryValue => {
    return value[itemId]?.[gp] || emptyEntry();
  };

  const updateEntry = (
    itemId: string,
    gp: "gp1" | "gp2",
    field: keyof InsuranceEntryValue,
    fieldValue: string | boolean
  ) => {
    const currentEntry = getEntry(itemId, gp);
    let updatedEntry = { ...currentEntry };

    if (field === "keineVorhanden" && fieldValue === true) {
      updatedEntry = {
        ...updatedEntry,
        kopie: false,
        foto: false,
        keineVorhanden: true,
        gesellschaft: "",
      };
    } else if ((field === "kopie" || field === "foto") && fieldValue === true) {
      updatedEntry = {
        ...updatedEntry,
        [field]: true,
        keineVorhanden: false,
      };
    } else {
      updatedEntry = { ...updatedEntry, [field]: fieldValue };
    }

    onChange({
      ...value,
      [itemId]: {
        ...value[itemId],
        [gp]: updatedEntry,
      },
    });
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const category = item.category || "Allgemein";
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {} as Record<string, InsuranceItem[]>);

  const renderEntry = (item: InsuranceItem, gp: "gp1" | "gp2") => {
    const entry = getEntry(item.id, gp);

    return (
      <div className="flex items-center gap-1.5">
        <Input
          value={entry.gesellschaft}
          onChange={(e) => updateEntry(item.id, gp, "gesellschaft", e.target.value)}
          placeholder="Gesellschaft"
          className="h-8 flex-1 min-w-0"
          disabled={entry.keineVorhanden}
        />
        
        <Button
          type="button"
          size="sm"
          variant={entry.kopie ? "default" : "outline"}
          className="h-8 w-8 p-0 shrink-0"
          onClick={() => updateEntry(item.id, gp, "kopie", !entry.kopie)}
          title="Kopie vorhanden"
          disabled={entry.keineVorhanden}
        >
          <FileText className="h-3.5 w-3.5" />
        </Button>
        
        <Button
          type="button"
          size="sm"
          variant={entry.foto ? "default" : "outline"}
          className="h-8 w-8 p-0 shrink-0"
          onClick={() => updateEntry(item.id, gp, "foto", !entry.foto)}
          title="Foto gemacht"
          disabled={entry.keineVorhanden}
        >
          <Camera className="h-3.5 w-3.5" />
        </Button>
        
        <label className="flex items-center gap-1 shrink-0 cursor-pointer">
          <Checkbox
            checked={entry.keineVorhanden}
            onCheckedChange={(checked) =>
              updateEntry(item.id, gp, "keineVorhanden", checked as boolean)
            }
          />
          <span className="text-xs text-muted-foreground">keine</span>
        </label>
      </div>
    );
  };

  return (
    <div className={cn("space-y-6", className)}>
      {Object.entries(groupedItems).map(([category, categoryItems]) => (
        <div key={category} className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-sm">{category}</h4>
          </div>

          {/* Header */}
          <div
            className="grid gap-3 text-xs text-muted-foreground"
            style={{
              gridTemplateColumns: showGP2 ? "140px 1fr 1fr" : "140px 1fr",
            }}
          >
            <div>Sparte</div>
            <div className="text-center">GP 1</div>
            {showGP2 && <div className="text-center">GP 2</div>}
          </div>

          {/* Rows */}
          {categoryItems.map((item) => (
            <div
              key={item.id}
              className="grid gap-3 items-center"
              style={{
                gridTemplateColumns: showGP2 ? "140px 1fr 1fr" : "140px 1fr",
              }}
            >
              <Label className="text-sm font-normal truncate" title={item.label}>
                {item.label}
              </Label>
              {renderEntry(item, "gp1")}
              {showGP2 && renderEntry(item, "gp2")}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
